import { inject, Injectable, WritableSignal } from '@angular/core';
import { produce } from 'immer';
import { defer, map, Observable, of, Subject } from 'rxjs';

import { CanvasElementId } from '@blaze-runtime/engine-domain';
import { CanvasElementDataProperties, getActiveBinding } from '@blaze-shared/elements-domain';
import { DatasetRow } from '@blaze-shared/schema-domain';
import { deepClone } from '@blaze-shared/shared-util';

import { RowGenerationService } from '../../services/row-generation.service';

export type DatasetRowWithIndex = { row: DatasetRow; dataIdx: number };
export type AddNewRowResult =
  | boolean
  | { emptyValue: boolean }
  | {
      newRow: DatasetRow;
      emptyRow: DatasetRow;
      shouldValidateFull: boolean;
      rowToValidate: { rowIndex: number; rowId: string };
    };

@Injectable({ providedIn: 'root' })
export class FormListRowsService {
  private readonly rowGenerationService = inject(RowGenerationService);

  private readonly emptyishRows = new Map<CanvasElementId, DatasetRowWithIndex[]>();
  private readonly emptyishRowsUpdated$$ = new Subject<{
    formListId: CanvasElementId;
    dataRow: DatasetRow;
  }>();
  /** form-lists datasources are workflows, so value changes not coming from
   * the source wf (ex. formula-refresh) aren't picked up */
  private readonly valueRowsUpdated$$ = new Subject<{
    formListId: CanvasElementId;
    dataRow: DatasetRow;
  }>();

  emptyishRowsUpdated$ = this.emptyishRowsUpdated$$.asObservable();
  valueRowsUpdated$ = this.valueRowsUpdated$$.asObservable();

  addNewRow(
    formListId: CanvasElementId,
    isFetchingNewId: WritableSignal<boolean>,
    dataProps: CanvasElementDataProperties[] | undefined,
    rowIdColumn: string | undefined,
    rowsCount: number | undefined,
    isInitial = false,
  ): Observable<AddNewRowResult> {
    return defer(() => {
      const binding = getActiveBinding(dataProps ?? []);
      if (!binding) {
        return of(false);
      }

      const emptyRow = this.rowGenerationService.getNewRow(binding.dataSource);
      if (!Object.keys(emptyRow).length) {
        return of(false);
      }

      isFetchingNewId.set(true);
      return this.rowGenerationService.generateRowId().pipe(
        map((newId) => {
          if (!rowIdColumn) {
            return false;
          }
          const newRow = {
            ...deepClone(emptyRow),
            [rowIdColumn]: newId,
            __isEmptyish: true,
            _isDeleted: false,
          };
          isFetchingNewId.set(false);
          const result: AddNewRowResult = {
            newRow,
            emptyRow,
            shouldValidateFull: isInitial,
            rowToValidate: {
              rowIndex: rowsCount ?? 0,
              rowId: newId,
            },
          };
          const updatedEmptyish = [
            ...(this.emptyishRows.get(formListId) ?? []),
            { row: newRow, dataIdx: result.rowToValidate.rowIndex },
          ];
          this.emptyishRows.set(formListId, updatedEmptyish);
          return result;
        }),
      );
    });
  }

  getFormListEmptyishRows(formListId: CanvasElementId): DatasetRowWithIndex[] | undefined {
    return this.emptyishRows.get(formListId);
  }

  removeEmptyishRows(formListId: CanvasElementId): void {
    this.emptyishRows.delete(formListId);
  }

  removeEmptyishRow(formListId: CanvasElementId, dataIdx: number): void {
    const rows = this.emptyishRows.get(formListId) ?? [];
    const updated = rows.filter((row) => row.dataIdx !== dataIdx);
    this.emptyishRows.set(formListId, updated);
  }

  attachEmptyishRows(formListId: CanvasElementId, data: DatasetRow[] | undefined): DatasetRow[] {
    const emptyish = this.emptyishRows.get(formListId) ?? [];
    let result = data ?? [];
    if (emptyish.length) {
      result = produce(result, (draft) => {
        for (const { dataIdx, row } of emptyish) {
          draft.splice(dataIdx, 0, row);
        }
      });
    }
    return result.map((row) => ({ ...row, _isDeleted: row._isDeleted ?? false }));
  }

  pruneEmptyishRows(formListId: CanvasElementId, rows: DatasetRow[] | undefined): DatasetRow[] {
    const emptyish: DatasetRowWithIndex[] = [...(this.emptyishRows.get(formListId) ?? [])];
    const data = rows?.filter((row, dataIdx) => {
      if (row.__isEmptyish) {
        const existingIdx = emptyish.findIndex((r) => r.dataIdx === dataIdx);
        if (existingIdx === -1) {
          emptyish.push({ dataIdx, row });
        } else {
          emptyish[existingIdx] = { dataIdx, row };
        }
      }
      return !row.__isEmptyish;
    });
    this.emptyishRows.set(formListId, emptyish);
    return data ?? [];
  }

  updateEmptyishRow(formListId: CanvasElementId, dataIdx: number, dataRow: DatasetRow): void {
    const rows = this.emptyishRows.get(formListId) ?? [];
    const updated = rows.map((row) => (row.dataIdx !== dataIdx ? row : { row: dataRow, dataIdx }));
    this.emptyishRows.set(formListId, updated);
    this.emptyishRowsUpdated$$.next({ formListId, dataRow });
  }

  updateValueDataRow(formListId: CanvasElementId, dataRow: DatasetRow): void {
    this.valueRowsUpdated$$.next({ formListId, dataRow });
  }
}
